import { api } from "@/api/client";
import { ThumbDownIcon, ThumbUpIcon, WarningIcon } from "@/components/Icons";
import { InfoDot, PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { useECharts } from "@/hooks/useECharts";
import { usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";
import { formatInt, formatPercent, modeLabel } from "@/lib/format";
import { includeTestSessions, selectedTenant, selectedWindow } from "@/state/filters";

// One colour per chat mode, plus one for a row whose mode is no registry id
// («unknown»), so no two series in this chart share a colour. Brand blue /
// orange / beige lead; navy, grey and a light blue tint carry the rest.
const MODE_COLORS = [
  "#002da5",
  "#fab400",
  "#e6d2b4",
  "#16213e",
  "#6b7280",
  "#7aa2ff",
];

// Funnel averages (documents, pages) read best with one decimal; a backend
// predating the funnel omits the field, which renders as an em dash.
function formatAvg(value: number | undefined): string {
  return value === undefined ? "—" : value.toFixed(1);
}

const RELEVANCE_LABELS = [
  "0.0–0.1",
  "0.1–0.2",
  "0.2–0.3",
  "0.3–0.4",
  "0.4–0.5",
  "0.5–0.6",
  "0.6–0.7",
  "0.7–0.8",
  "0.8–0.9",
  "0.9–1.0",
];

export function QualityView() {
  const { data, loading, error, lastFetched, refetch } = usePoll(
    (s) =>
      api.ragAudit(
        {
          window: selectedWindow.value,
          includeTest: includeTestSessions.value,
          tenant: selectedTenant.value,
        },
        s,
      ),
    [selectedWindow.value, includeTestSessions.value, selectedTenant.value],
  );

  const relevanceOption = data
    ? {
        title: { text: t("quality.chart.relevance"), left: "center", textStyle: { fontSize: 14 } },
        legend: { bottom: 0 },
        tooltip: { trigger: "axis" },
        xAxis: { type: "category", data: RELEVANCE_LABELS },
        yAxis: { type: "value" },
        series: Object.entries(data.relevance_histogram_by_mode).map(
          ([mode, buckets], i) => ({
            name: modeLabel(mode),
            type: "bar",
            stack: "all",
            data: buckets,
            itemStyle: { color: MODE_COLORS[i % MODE_COLORS.length] },
          }),
        ),
      }
    : null;
  const relevanceRef = useECharts(relevanceOption);

  return (
    <div style="display: grid; gap: 24px;">
      <div style="display: flex; align-items: center; gap: 24px;">
        <Staleness
          lastFetched={lastFetched}
          loading={loading}
          error={error}
          onRefresh={refetch}
        />
      </div>

      <div class="grid grid-3">
        <div class="card">
          <h3>
            {t("quality.noAnswerRate")} <InfoDot k="quality.noAnswerRate" />
          </h3>
          <div class="kpi">
            {data?.no_answer_rate_by_mode.overall == null
              ? "—"
              : formatPercent(data.no_answer_rate_by_mode.overall)}
          </div>
          <div class="kpi-sub">{t("quality.noAnswerSub")}</div>
        </div>
        <div class="card">
          <h3>
            {t("quality.streamingFailures")} <InfoDot k="quality.streamingFailures" />
          </h3>
          <div class="kpi">{formatInt(data?.streaming_failures ?? 0)}</div>
          <div class="kpi-sub">{t("quality.streamingSub")}</div>
        </div>
        <div class="card">
          <h3>
            {t("quality.verifier")} <InfoDot k="quality.verifier" />
          </h3>
          <div class="kpi">
            {data?.verifier.verified_turns ?? 0} /{" "}
            {(data?.verifier.verified_turns ?? 0) + (data?.verifier.unverified_turns ?? 0)}
          </div>
          <div class="kpi-sub">
            {t("quality.avgConfidence", {
              v:
                data?.verifier.avg_confidence == null
                  ? "—"
                  : data.verifier.avg_confidence.toFixed(2),
            })}
          </div>
        </div>
      </div>

      <section class="card">
        <h2>{t("quality.relevance")}</h2>
        <PanelHelp k="quality.relevance" />
        <div ref={relevanceRef} class="chart chart-tall" />
      </section>

      <section class="card">
        <h2>{t("quality.funnel")}</h2>
        <PanelHelp k="quality.funnel" />
        <table class="table">
          <thead>
            <tr>
              <th>{t("common.mode")}</th>
              <th>{t("common.turns")}</th>
              <th>{t("quality.col.docsFound")}</th>
              <th>{t("quality.col.pagesForwarded")}</th>
              <th>{t("quality.col.pagesCited")}</th>
              <th>{t("quality.col.fillerDropped")}</th>
              <th>{t("quality.col.avgContext")}</th>
              <th>{t("quality.col.snippetOnly")}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data?.context_depth_by_mode ?? {})
              .sort((a, b) => b[1].turns - a[1].turns)
              .map(([mode, d]) => (
                <tr key={mode}>
                  <td>{modeLabel(mode)}</td>
                  <td>{formatInt(d.turns)}</td>
                  <td>{formatAvg(d.avg_docs_returned)}</td>
                  <td>{formatAvg(d.avg_pages_forwarded)}</td>
                  <td>{formatAvg(d.avg_sources_cited)}</td>
                  <td>{formatInt(d.floor_turns ?? 0)}</td>
                  <td>{formatInt(Math.round(d.avg_context_chars))}</td>
                  <td>
                    {d.snippet_fallback_turns > 0 ? (
                      <span class="hl-orange">
                        <WarningIcon />
                        {formatInt(d.snippet_fallback_turns)}
                      </span>
                    ) : (
                      "0"
                    )}
                  </td>
                </tr>
              ))}
            {Object.keys(data?.context_depth_by_mode ?? {}).length === 0 && (
              <tr>
                <td colspan={8} class="muted">
                  {t("quality.noRetrieval")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section class="card">
        <h2>
          {t("quality.topCited")} <InfoDot k="quality.sanitizerHits" />
        </h2>
        <PanelHelp k="quality.topCited" />
        <table class="table">
          <thead>
            <tr>
              <th>{t("quality.col.url")}</th>
              <th>{t("quality.col.title")}</th>
              <th>
                {t("common.sessions")} <InfoDot k="quality.sessionsCount" />
              </th>
              <th>
                <ThumbUpIcon label={t("common.ratingPositive")} />/
                <ThumbDownIcon label={t("common.ratingNegative")} />
              </th>
              <th>{t("quality.col.sanitizer")}</th>
            </tr>
          </thead>
          <tbody>
            {data?.top_cited_sources.slice(0, 25).map((s) => {
              const fb = data.source_feedback.find((f) => f.url === s.url);
              const san = data.rag_sanitized_by_source[s.url] ?? 0;
              return (
                <tr key={s.url}>
                  <td style="word-break: break-all;">
                    <a href={s.url} target="_blank" rel="noreferrer">
                      {s.url}
                    </a>
                  </td>
                  <td>{s.title}</td>
                  <td>{formatInt(s.count)}</td>
                  <td>
                    {fb ? `${formatInt(fb.positive)} / ${formatInt(fb.negative)}` : "—"}
                  </td>
                  <td>
                    {san > 0 ? (
                      <span class="hl-orange">
                        <WarningIcon />
                        {formatInt(san)}
                      </span>
                    ) : (
                      "0"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section class="card">
        <h2>{t("quality.offers")}</h2>
        <div class="muted" style="margin-top: 4px; margin-bottom: 12px; font-size: 12px;">
          {t("quality.offersNote")}
        </div>
        <div class="grid" style="grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));">
          <div>
            <h3>
              {t("quality.topPlaces")} <InfoDot k="quality.offerPlaces" />
            </h3>
            <PanelHelp k="quality.offerPlaces" />
            <table class="table">
              <thead>
                <tr>
                  <th>{t("quality.place")}</th>
                  <th>{t("common.sessions")}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data?.offer_places ?? {})
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 20)
                  .map(([ort, count]) => (
                    <tr key={ort}>
                      <td>{ort}</td>
                      <td>{formatInt(count)}</td>
                    </tr>
                  ))}
                {Object.keys(data?.offer_places ?? {}).length === 0 && (
                  <tr>
                    <td colspan={2} class="muted">
                      {t("quality.noPlaces")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div>
            <h3>
              {t("quality.topProfessions")} <InfoDot k="quality.offerProfessions" />
            </h3>
            <PanelHelp k="quality.offerProfessions" />
            <table class="table">
              <thead>
                <tr>
                  <th>{t("quality.profession")}</th>
                  <th>{t("common.sessions")}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data?.offer_professions ?? {})
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 20)
                  .map(([beruf, count]) => (
                    <tr key={beruf}>
                      <td>{beruf}</td>
                      <td>{formatInt(count)}</td>
                    </tr>
                  ))}
                {Object.keys(data?.offer_professions ?? {}).length === 0 && (
                  <tr>
                    <td colspan={2} class="muted">
                      {t("quality.noProfessions")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section class="card">
        <h2>{t("quality.contentGaps")}</h2>
        <PanelHelp k="quality.contentGaps" />
        <table class="table">
          <thead>
            <tr>
              <th>{t("quality.query")}</th>
              <th>{t("common.count")}</th>
            </tr>
          </thead>
          <tbody>
            {data?.content_gap_queries.slice(0, 30).map((g) => (
              <tr key={g.query}>
                <td>{g.query}</td>
                <td>{formatInt(g.count)}</td>
              </tr>
            ))}
            {(!data || data.content_gap_queries.length === 0) && (
              <tr>
                <td colspan={2} class="muted">
                  {t("quality.noGaps")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section class="card">
        <h2>{t("quality.guardrailReasons")}</h2>
        <PanelHelp k="quality.guardrailReasons" />
        <table class="table">
          <thead>
            <tr>
              <th>{t("common.reason")}</th>
              <th>{t("common.count")}</th>
            </tr>
          </thead>
          <tbody>
            {data &&
              Object.entries(data.guardrail_reasons)
                .sort((a, b) => b[1] - a[1])
                .map(([reason, count]) => (
                  <tr key={reason}>
                    <td>{reason}</td>
                    <td>{formatInt(count)}</td>
                  </tr>
                ))}
            {(!data || Object.keys(data.guardrail_reasons).length === 0) && (
              <tr>
                <td colspan={2} class="muted">
                  {t("quality.noGuardrail")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section class="card">
        <h2>{t("quality.verifierIssues")}</h2>
        <PanelHelp k="quality.verifier" />
        <table class="table">
          <thead>
            <tr>
              <th>{t("quality.issue")}</th>
              <th>{t("common.count")}</th>
            </tr>
          </thead>
          <tbody>
            {data?.verifier.top_issues.map(([issue, count]) => (
              <tr key={issue}>
                <td>{issue}</td>
                <td>{formatInt(count)}</td>
              </tr>
            ))}
            {(!data || data.verifier.top_issues.length === 0) && (
              <tr>
                <td colspan={2} class="muted">
                  {t("quality.noVerifier")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
